import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface FormData {
  name: string;
  phone: string;
  pickup_suburb: string;
  dropoff_suburb: string;
  move_date: string;
  details: string;
}

const initialForm: FormData = {
  name: "",
  phone: "",
  pickup_suburb: "",
  dropoff_suburb: "",
  move_date: "",
  details: "",
};

const QuoteForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const validate = (): boolean => {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.phone.trim()) e.phone = "Phone number is required";
    else if (!/^[\d\s()+-]{7,20}$/.test(form.phone.trim()))
      e.phone = "Enter a valid phone number";
    if (!form.pickup_suburb.trim()) e.pickup_suburb = "Pickup suburb is required";
    if (!form.dropoff_suburb.trim()) e.dropoff_suburb = "Drop-off suburb is required";
    if (!form.move_date) e.move_date = "Move date is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    const { error } = await supabase.from("quote_requests").insert({
      name: form.name.trim(),
      phone: form.phone.trim(),
      pickup_suburb: form.pickup_suburb.trim(),
      dropoff_suburb: form.dropoff_suburb.trim(),
      move_date: form.move_date,
      details: form.details.trim() || null,
    });
    setLoading(false);

    if (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
      return;
    }

    setSubmitted(true);
  };

  const inputClass = (field: keyof FormData) =>
    `w-full px-4 py-3.5 border-2 rounded-md bg-white text-[#1a1a1a] font-medium placeholder:text-[#1a1a1a]/30 focus:border-[#FFC300] focus:outline-none transition-colors ${
      errors[field] ? "border-red-400" : "border-[#1a1a1a]/10"
    }`;

  return (
    <section id="quote" className="bg-[#f7f7f7] py-16 md:py-20">
      <div className="container max-w-2xl">
        <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] text-center mb-3">
          Get Your Moving Quote in 60 Seconds
        </h2>
        <p className="text-center text-[#1a1a1a]/75 mb-6 text-base">
          Tell us a few details and we'll get back to you quickly with pricing and availability.
        </p>
        <p className="text-center text-xs font-semibold text-[#1a1a1a]/70 tracking-wide mb-10">
          Fast response &nbsp;•&nbsp; No hidden fees &nbsp;•&nbsp; Fully insured
        </p>

        {submitted ? (
          <div className="bg-[#e8f5e9] border-2 border-[#4caf50] rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2">
              Thanks - we've received your quote request!
            </h3>
            <p className="text-[#1a1a1a]/60">
              We'll be in touch shortly with pricing and availability.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className={inputClass("name")}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={form.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className={inputClass("phone")}
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <input
                  type="text"
                  placeholder="Pickup Suburb"
                  value={form.pickup_suburb}
                  onChange={(e) => handleChange("pickup_suburb", e.target.value)}
                  className={inputClass("pickup_suburb")}
                />
                {errors.pickup_suburb && <p className="text-red-500 text-xs mt-1">{errors.pickup_suburb}</p>}
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Drop-off Suburb"
                  value={form.dropoff_suburb}
                  onChange={(e) => handleChange("dropoff_suburb", e.target.value)}
                  className={inputClass("dropoff_suburb")}
                />
                {errors.dropoff_suburb && <p className="text-red-500 text-xs mt-1">{errors.dropoff_suburb}</p>}
              </div>
            </div>
            <div>
              <input
                type="date"
                aria-label="Move date"
                value={form.move_date}
                onChange={(e) => handleChange("move_date", e.target.value)}
                className={inputClass("move_date")}
              />
              {errors.move_date && <p className="text-red-500 text-xs mt-1">{errors.move_date}</p>}
            </div>
            <textarea
              placeholder="Any details about your move? (optional)"
              aria-label="Move details"
              rows={3}
              value={form.details}
              onChange={(e) => handleChange("details", e.target.value)}
              className="w-full px-4 py-3.5 border-2 border-[#1a1a1a]/10 rounded-md bg-white text-[#1a1a1a] font-medium placeholder:text-[#1a1a1a]/30 focus:border-[#FFC300] focus:outline-none transition-colors resize-none"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1a1a1a] text-[#FFC300] py-4 text-lg font-bold rounded-md hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {loading ? "Submitting…" : "Get My Free Quote"}
            </button>
            <p className="text-center text-xs text-[#1a1a1a]/70">
              No obligation. We'll respond quickly.
            </p>
          </form>
        )}
      </div>
    </section>
  );
};

export default QuoteForm;
